import { useCallback, useEffect, useRef } from "react";
import Modify from "ol/interaction/Modify";
import Snap from "ol/interaction/Snap";
import { altShiftKeysOnly } from "ol/events/condition";
import { Collection } from "ol";
import { ModifyEvent } from "ol/interaction/Modify";
import Map from "ol/Map";
import Feature from "ol/Feature";
import { Geometry } from "ol/geom";

interface UseModifyInteractionCallbacks {
  onStart: () => void;
  onEnd: (event: ModifyEvent) => void;
}

/**
 * OL Modify + Snap 인터랙션의 설치/해제 라이프사이클을 관리합니다.
 * install(feature)로 수정 핸들을 붙이고, remove()로 제거합니다.
 */
export function useModifyInteraction(
  map: Map,
  { onStart, onEnd }: UseModifyInteractionCallbacks
) {
  const modifyRef = useRef<Modify | null>(null);
  const snapRef = useRef<Snap | null>(null);

  // 안정적인 래퍼를 통해 최신 콜백을 항상 호출 (OL 이벤트 재등록 불필요)
  const onStartRef = useRef(onStart);
  const onEndRef = useRef(onEnd);
  onStartRef.current = onStart;
  onEndRef.current = onEnd;

  const stableOnStart = useCallback(() => onStartRef.current(), []);
  const stableOnEnd = useCallback((e: ModifyEvent) => onEndRef.current(e), []);

  const remove = useCallback(() => {
    if (modifyRef.current) {
      modifyRef.current.un("modifystart", stableOnStart);
      modifyRef.current.un("modifyend", stableOnEnd);
      map.removeInteraction(modifyRef.current);
      modifyRef.current = null;
    }
    if (snapRef.current) {
      map.removeInteraction(snapRef.current);
      snapRef.current = null;
    }
  }, [map, stableOnStart, stableOnEnd]);

  const install = useCallback(
    (feature: Feature<Geometry>) => {
      remove();

      const features = new Collection([feature]);
      modifyRef.current = new Modify({
        features,
        deleteCondition: altShiftKeysOnly,
      });
      snapRef.current = new Snap({ features });

      modifyRef.current.on("modifystart", stableOnStart);
      modifyRef.current.on("modifyend", stableOnEnd);
      map.addInteraction(snapRef.current);
      map.addInteraction(modifyRef.current);
    },
    [map, remove, stableOnStart, stableOnEnd]
  );

  const getInstalledFeatureId = useCallback((): string | undefined => {
    const features = (modifyRef.current as any)?.features_;
    if (features?.getLength() > 0) {
      return String(features.getArray()[0].getId());
    }
    return undefined;
  }, []);

  useEffect(() => () => remove(), [remove]);

  return { install, remove, getInstalledFeatureId };
}
